export function EdmontonRules() {
  const requirements = [
    {
      icon: 'ri-star-line',
      title: 'Most Investor-Friendly City',
      description: 'Edmonton is currently the most investor-friendly major city in Canada. No principal residence requirement—operate STRs on investment properties freely.',
      highlight: true
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Simple Business License',
      description: 'Annual business license costs approximately $250. Straightforward application process with minimal bureaucracy.',
      highlight: true
    },
    {
      icon: 'ri-file-text-line',
      title: 'Guest Management Plan Required',
      description: 'Must submit a comprehensive Guest Management Plan covering parking arrangements, noise control measures, and emergency procedures.',
      highlight: false
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'No Night Limits',
      description: 'Operate your STR 365 nights per year with no restrictions. Full flexibility for year-round operations.',
      highlight: false
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Nuisance-Focused Enforcement',
      description: 'Enforcement targets "nuisance" properties. Penalties for noise violations, trash issues, and parking problems start at $500+.',
      highlight: false
    },
    {
      icon: 'ri-fire-line',
      title: 'Fire Safety Standards',
      description: 'Smoke detectors, fire extinguishers, and emergency exit plans required. Visual floor plan must be submitted with application.',
      highlight: false
    }
  ];

  const guestPlanComponents = [
    {
      icon: 'ri-parking-box-line',
      title: 'Parking Management',
      description: 'Clearly designate parking spaces for guests. Provide instructions on where guests can park and any restrictions.'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Noise Control',
      description: 'Establish quiet hours (typically 10 PM - 8 AM). Provide contact information for noise complaints and outline consequences.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Emergency Contact',
      description: 'Provide 24/7 contact information for emergencies. Include local property manager or owner contact details.'
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Waste Management',
      description: 'Clear instructions on garbage and recycling procedures. Specify collection days and bin locations.'
    }
  ];

  const marketHotspots = [
    {
      name: 'Old Strathcona',
      description: 'Historic district with vibrant arts scene, theaters, and restaurants. High demand from leisure travelers and festival attendees.',
      revenue: 'Exceptional ROI',
      icon: 'ri-building-line'
    },
    {
      name: 'West Edmonton Mall Area',
      description: 'Near North America\'s largest shopping mall. Attracts families and tourists year-round. Consistent booking rates.',
      revenue: 'Year-round demand',
      icon: 'ri-shopping-bag-line'
    },
    {
      name: 'Downtown Core',
      description: 'Business district with high demand from corporate travelers. Close to Rogers Place arena and convention center.',
      revenue: 'Steady business travel',
      icon: 'ri-building-2-line'
    }
  ];

  const enforcementTypes = [
    {
      violation: 'Noise Complaints',
      penalty: '$500+ per incident',
      severity: 'high'
    },
    {
      violation: 'Parking Violations',
      penalty: '$500+ fines',
      severity: 'high'
    },
    {
      violation: 'Trash/Waste Issues',
      penalty: '$500+ penalties',
      severity: 'medium'
    },
    {
      violation: 'Missing License',
      penalty: 'Cease operations order',
      severity: 'high'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Edmonton STR Regulations 2026
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Edmonton stands out as Canada's most investor-friendly major city for short-term rentals. Simple licensing, no principal residence requirement, and exceptional ROI in key neighborhoods.
          </p>
        </div>

        {/* Key Requirements */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {requirements.map((req, index) => (
            <div 
              key={index}
              className={`p-8 rounded-2xl transition-all ${
                req.highlight 
                  ? 'bg-[#882629] text-white shadow-xl' 
                  : 'bg-white hover:shadow-lg'
              }`}
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-xl mb-6 ${
                req.highlight 
                  ? 'bg-white/20' 
                  : 'bg-[#882629]/10'
              }`}>
                <i className={`${req.icon} text-3xl ${
                  req.highlight ? 'text-white' : 'text-[#882629]'
                }`}></i>
              </div>
              <h3 className={`text-xl font-semibold mb-4 ${
                req.highlight ? 'text-white' : 'text-[#505A5A]'
              }`}>
                {req.title}
              </h3>
              <p className={`leading-relaxed ${
                req.highlight ? 'text-white/90' : 'text-[#505A5A]/70'
              }`}>
                {req.description}
              </p>
            </div>
          ))}
        </div>

        {/* Guest Management Plan */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 mb-20">
          <h3 className="text-3xl font-semibold text-[#505A5A] mb-4 text-center">
            Guest Management Plan Requirements
          </h3>
          <p className="text-center text-[#505A5A]/70 mb-12 max-w-2xl mx-auto">
            Edmonton requires a comprehensive Guest Management Plan as part of your business license application. Here's what you need to include:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guestPlanComponents.map((component, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#882629]/10 rounded-full mx-auto mb-4">
                  <i className={`${component.icon} text-3xl text-[#882629]`}></i>
                </div>
                <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {component.title}
                </h4>
                <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                  {component.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Market Hotspots */}
        <div className="mb-20">
          <h3 className="text-3xl font-semibold text-[#505A5A] mb-12 text-center">
            Top Edmonton STR Markets
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {marketHotspots.map((spot, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl hover:shadow-xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-[#882629]/10 rounded-xl mb-6">
                  <i className={`${spot.icon} text-3xl text-[#882629]`}></i>
                </div>
                <h4 className="text-xl font-semibold text-[#882629] mb-3">
                  {spot.name}
                </h4>
                <p className="text-[#505A5A]/70 mb-4 leading-relaxed">
                  {spot.description}
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#505A5A]">
                  <i className="ri-line-chart-line text-[#882629]"></i>
                  {spot.revenue}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ROI Highlight */}
        <div className="bg-gradient-to-r from-[#882629] to-[#A63234] text-white rounded-2xl p-8 lg:p-12 mb-12">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-funds-line text-5xl text-white"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-2xl font-semibold mb-3">
                Exceptional Year-Round ROI
              </h3>
              <p className="text-white/90 leading-relaxed mb-4">
                Old Strathcona and properties near West Edmonton Mall consistently deliver the highest ROI in Edmonton. Lower property costs combined with strong year-round demand create excellent investment opportunities.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Lower Entry Costs</span>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">No PR Requirement</span>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">365-Night Operations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enforcement Table */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 mb-12">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            Nuisance-Focused Enforcement: Common Violations
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-[#882629]">
                  <th className="text-left py-4 px-4 text-[#505A5A] font-semibold">Violation Type</th>
                  <th className="text-left py-4 px-4 text-[#505A5A] font-semibold">Penalty</th>
                  <th className="text-left py-4 px-4 text-[#505A5A] font-semibold">Severity</th>
                </tr>
              </thead>
              <tbody>
                {enforcementTypes.map((item, index) => (
                  <tr key={index} className="border-b border-[#505A5A]/10">
                    <td className="py-4 px-4 text-[#505A5A] font-medium">{item.violation}</td>
                    <td className="py-4 px-4 text-[#505A5A]">{item.penalty}</td>
                    <td className="py-4 px-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                        item.severity === 'high' 
                          ? 'bg-[#882629]/10 text-[#882629]' 
                          : 'bg-[#505A5A]/10 text-[#505A5A]'
                      }`}>
                        {item.severity === 'high' ? 'High' : 'Medium'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-[#882629]/5 border-l-4 border-[#882629] p-6 rounded-lg">
          <div className="flex items-start gap-4">
            <i className="ri-alert-line text-2xl text-[#882629] flex-shrink-0 mt-1"></i>
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-2">Maintain Good Neighbor Relations</h4>
              <p className="text-[#505A5A]/70 leading-relaxed">
                Edmonton's enforcement is complaint-driven and focuses on nuisance properties. Maintain excellent guest management practices to avoid $500+ penalties for noise, parking, and waste violations. A well-managed property rarely faces enforcement action.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
